import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { SITE, SERVICES } from "@/lib/site";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = { name: "", email: "", phone: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#0b2a5b] grain overflow-hidden" data-testid="contact-section">
      <img src={SITE.images.location} alt="Grand Rapids skyline" className="absolute inset-0 h-full w-full object-cover opacity-15" />
      <div className="absolute inset-0 bg-[#0b2a5b]/70" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="overline text-[#8fb4f5] mb-4">Get In Touch</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-white leading-tight">
              Begin the conversation.
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Schedule a complimentary, no-obligation consultation with a Sagos advisor.
              We&apos;ll respond within one business day.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <ul className="mt-10 space-y-6">
              <li className="flex gap-4">
                <MapPin className="text-[#8fb4f5] shrink-0" />
                <div className="text-slate-200">
                  <p>{SITE.address.line1}</p>
                  <p>{SITE.address.line2}</p>
                </div>
              </li>
              <li className="flex gap-4 items-center">
                <Phone className="text-[#8fb4f5] shrink-0" />
                <a href={SITE.phoneHref} className="text-slate-200 hover:text-white transition-colors" data-testid="contact-phone">{SITE.phone}</a>
              </li>
              <li className="flex gap-4 items-center">
                <Mail className="text-[#8fb4f5] shrink-0" />
                <a href={`mailto:${SITE.email}`} className="text-slate-200 hover:text-white transition-colors">{SITE.email}</a>
              </li>
              <li className="flex gap-4 items-center">
                <Clock className="text-[#8fb4f5] shrink-0" />
                <span className="text-slate-200">Mon–Fri · 8:00am – 6:00pm ET</span>
              </li>
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white rounded-3xl p-7 md:p-10 shadow-2xl"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="c-name">Full name</Label>
                <Input id="c-name" data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-email">Email</Label>
                <Input id="c-email" data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@email.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-phone">Phone</Label>
                <Input id="c-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
              </div>
              <div className="space-y-2">
                <Label>Area of interest</Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger data-testid="contact-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.key} value={s.title}>{s.title}</SelectItem>
                    ))}
                    <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2 mt-5">
              <Label htmlFor="c-msg">How can we help?</Label>
              <Textarea id="c-msg" data-testid="contact-message" required rows={4} value={form.message} onChange={set("message")} placeholder="Tell us a little about your goals..." />
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-primary text-primary-foreground font-medium hover:bg-[#0b2a5b] transition-colors duration-200 disabled:opacity-60"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              Request Consultation
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
